import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import PayoutsApiService from "@/core/services/api.service.payouts";

export default {
  props: {},
  name: "payouts",
  components: {},
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 10
      },
      total: 0,
      totalPages: 0,
      payoutRequests: [],
      loading: false,
      componentLoaded: false,
      // Bulk selection
      selectedItems: [],
      selectAll: false,
      bulkApproveDialog: false,
      bulkRejectDialog: false,
      bulkRejectReason: "",
      // Filters
      statusFilter: "",
      providerFilter: "",
      customerEmailFilter: "",
      customerCodeFilter: "",
      requestedAtFrom: null,
      requestedAtTo: null,
      requestedAtFromMenu: false,
      requestedAtToMenu: false,
      // Filter options
      statusOptions: [
        { text: "All", value: "" },
        { text: "Pending Approval", value: "PENDING_APPROVAL" },
        { text: "Processing", value: "PROCESSING" },
        { text: "Paid", value: "PAID" },
        { text: "Rejected", value: "REJECTED" }
      ],
      providerOptions: [
        { text: "All", value: "" },
        { text: "Stripe", value: "Stripe" }
      ],
      // Detail dialog
      detailDialog: false,
      selectedRequest: null,
      detailLoading: false,
      // Action dialogs
      actionLoading: false,
      approveDialog: false,
      approveTargetId: null,
      rejectDialog: false,
      rejectReason: "",
      rejectTargetId: null,
      // Table headers
      headers: [
        { text: "ID", value: "id", sortable: false, width: "70px" },
        { text: "Customer Email", value: "customerEmail", sortable: false },
        { text: "Amount", value: "amount", sortable: false },
        { text: "Provider", value: "provider", sortable: false },
        {
          text: "Payout Ready",
          value: "payoutAccountReady",
          sortable: false,
          align: "center",
          width: "100px"
        },
        { text: "Requested At", value: "requestedAt", sortable: false },
        { text: "Updated At", value: "updatedAt", sortable: false },
        {
          text: "Transfers",
          value: "transferCount",
          sortable: false,
          align: "center",
          width: "80px"
        },
        { text: "Status", value: "status", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "180px" }
      ]
    };
  },
  watch: {
    pagination: {
      handler() {
        if (!this.componentLoaded) return;
        this.fetchPayoutRequests();
      },
      deep: true
    }
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Finance" }, { title: "Payouts" }]);
    await PayoutsApiService.init();
    this.componentLoaded = true;
    this.fetchPayoutRequests();
  },
  methods: {
    async fetchPayoutRequests() {
      if (this.loading) return;

      const params = {
        page: this.pagination.page - 1,
        size: this.pagination.itemsPerPage
      };

      if (this.statusFilter) {
        params.status = this.statusFilter;
      }
      if (this.providerFilter) {
        params.provider = this.providerFilter;
      }
      if (this.customerEmailFilter) {
        params.customerEmail = this.customerEmailFilter.trim();
      }
      if (this.customerCodeFilter) {
        params.customerCode = this.customerCodeFilter.trim();
      }
      if (this.requestedAtFrom) {
        params.requestedAtFrom = this.requestedAtFrom + "T00:00:00";
      }
      if (this.requestedAtTo) {
        params.requestedAtTo = this.requestedAtTo + "T23:59:59";
      }

      this.loading = true;
      try {
        const response = await PayoutsApiService.getPayoutRequests(params);
        if (response?.data) {
          this.payoutRequests = response.data.items || [];
          this.total = response.data.totalElements || 0;
          this.totalPages = response.data.totalPages || 0;
        }
      } catch (error) {
        console.error("Error loading payout requests:", error);
        this.showToast("Failed to load payout requests", "danger");
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.pagination.page = 1;
      this.selectedItems = [];
      this.selectAll = false;
      this.fetchPayoutRequests();
    },

    clearCustomerEmailFilter() {
      this.customerEmailFilter = "";
      this.pagination.page = 1;
      this.fetchPayoutRequests();
    },

    clearCustomerCodeFilter() {
      this.customerCodeFilter = "";
      this.pagination.page = 1;
      this.fetchPayoutRequests();
    },

    clearRequestedAtFrom() {
      this.requestedAtFrom = null;
      this.pagination.page = 1;
      this.fetchPayoutRequests();
    },

    clearRequestedAtTo() {
      this.requestedAtTo = null;
      this.pagination.page = 1;
      this.fetchPayoutRequests();
    },

    resetFilters() {
      this.statusFilter = "PENDING_APPROVAL";
      this.providerFilter = "";
      this.customerEmailFilter = "";
      this.customerCodeFilter = "";
      this.requestedAtFrom = null;
      this.requestedAtTo = null;
      this.pagination.page = 1;
      this.fetchPayoutRequests();
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.payoutRequests
          .filter(item => item.payoutAccountReady)
          .map(item => item.id);
      } else {
        this.selectedItems = [];
      }
    },

    toggleItemSelection(itemId) {
      const index = this.selectedItems.indexOf(itemId);
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(itemId);
      }
      const selectableItems = this.payoutRequests.filter(item => item.payoutAccountReady);
      this.selectAll =
        selectableItems.length > 0 && this.selectedItems.length === selectableItems.length;
    },

    openBulkApproveDialog() {
      if (this.selectedItems.length === 0) return;
      this.bulkApproveDialog = true;
    },

    async submitBulkApprove() {
      this.actionLoading = true;
      try {
        await PayoutsApiService.bulkApprove(this.selectedItems);
        const count = this.selectedItems.length;
        this.showToast(
          `Successfully approved ${count} payout request${count > 1 ? "s" : ""}`,
          "success"
        );
        this.bulkApproveDialog = false;
        this.selectedItems = [];
        this.selectAll = false;
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to approve payout requests";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    openBulkRejectDialog() {
      if (this.selectedItems.length === 0) return;
      this.bulkRejectReason = "";
      this.bulkRejectDialog = true;
    },

    async submitBulkReject() {
      if (!this.bulkRejectReason.trim()) {
        this.showToast("Rejection reason is required", "warning");
        return;
      }
      this.actionLoading = true;
      try {
        await PayoutsApiService.bulkReject(this.selectedItems, this.bulkRejectReason.trim());
        const count = this.selectedItems.length;
        this.showToast(
          `Successfully rejected ${count} payout request${count > 1 ? "s" : ""}`,
          "success"
        );
        this.bulkRejectDialog = false;
        this.selectedItems = [];
        this.selectAll = false;
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to reject payout requests";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    async openDetail(item) {
      this.detailLoading = true;
      this.detailDialog = true;
      try {
        const response = await PayoutsApiService.getPayoutRequestById(item.id);
        this.selectedRequest = response.data;
      } catch (error) {
        console.error("Error loading payout detail:", error);
        this.showToast("Failed to load payout details", "danger");
        this.detailDialog = false;
      } finally {
        this.detailLoading = false;
      }
    },

    closeDetail() {
      this.detailDialog = false;
      this.selectedRequest = null;
    },

    openApproveDialog(id) {
      this.approveTargetId = id;
      this.approveDialog = true;
    },

    async submitApprove() {
      this.actionLoading = true;
      try {
        await PayoutsApiService.approvePayoutRequest(this.approveTargetId);
        this.showToast("Payout request approved successfully", "success");
        this.approveDialog = false;
        this.closeDetail();
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to approve payout request";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    openRejectDialog(id) {
      this.rejectTargetId = id;
      this.rejectReason = "";
      this.rejectDialog = true;
    },

    async submitReject() {
      if (!this.rejectReason.trim()) {
        this.showToast("Rejection reason is required", "warning");
        return;
      }
      this.actionLoading = true;
      try {
        await PayoutsApiService.rejectPayoutRequest(this.rejectTargetId, this.rejectReason.trim());
        this.showToast("Payout request rejected", "success");
        this.rejectDialog = false;
        this.closeDetail();
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to reject payout request";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    async retryRequest(id) {
      this.actionLoading = true;
      try {
        await PayoutsApiService.retryPayoutRequest(id);
        this.showToast("Payout request retried successfully", "success");
        this.closeDetail();
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to retry payout request";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    async syncRequest(id) {
      this.actionLoading = true;
      try {
        await PayoutsApiService.syncPayoutRequest(id);
        this.showToast("Payout request synced", "success");
        if (this.selectedRequest && this.selectedRequest.id === id) {
          const response = await PayoutsApiService.getPayoutRequestById(id);
          this.selectedRequest = response.data;
        }
        this.fetchPayoutRequests();
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to sync payout request";
        this.showToast(message, "danger");
      } finally {
        this.actionLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },

    formatAmount(amount, currency) {
      if (amount == null) return "-";
      const sym = currency?.toUpperCase() === "EUR" ? "€" : currency?.toUpperCase() || "";
      return `${sym}${Number(amount).toFixed(2)}`;
    },

    getStatusColor(status) {
      const colors = {
        PENDING_APPROVAL: "orange",
        PROCESSING: "indigo",
        PAID: "teal",
        REJECTED: "red"
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        PENDING_APPROVAL: "Pending Approval",
        PROCESSING: "Processing",
        PAID: "Paid",
        REJECTED: "Rejected"
      };
      return texts[status] || status;
    },

    canApprove(item) {
      return item.status === "PENDING_APPROVAL" && item.payoutAccountReady;
    },

    canReject(item) {
      return item.status === "PENDING_APPROVAL";
    },

    canRetry(item) {
      return (item.status === "FAILED" || item.status === "REVERSED") && item.payoutAccountReady;
    },

    canSync(item) {
      return item.status === "PROCESSING";
    },

    showApproveButton(item) {
      return item.status === "PENDING_APPROVAL";
    },

    showRejectButton(item) {
      return item.status === "PENDING_APPROVAL";
    },

    showRetryButton(item) {
      return item.status === "FAILED" || item.status === "REVERSED";
    },

    showToast(message, variant) {
      this.$bvToast?.toast(message, {
        variant: variant,
        solid: true,
        autoHideDelay: 4000
      });
    }
  },
  computed: {
    name() {
      return "Payouts";
    },
    computedHeaders() {
      if (this.isPendingApprovalFilter) {
        return [{ text: "", value: "checkbox", sortable: false, width: "50px" }, ...this.headers];
      }
      return this.headers;
    },
    isPendingApprovalFilter() {
      return this.statusFilter === "PENDING_APPROVAL";
    },
    hasSelectedItems() {
      return this.selectedItems.length > 0;
    }
  }
};
